import { NavLink, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { rbac } from '@dha-nexus/shared';

import { usePageMetadata } from '../../app/PageMetaContext.jsx';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { api } from '../../lib/apiClient.js';
import { useApi, useMutation } from '../../lib/useApi.js';
import {
  ChipMultiSelect,
  EmptyState,
  ErrorState,
  FormField,
  LoadingSkeleton,
  PageHeader,
  Select,
  StatusChip,
  Textarea,
  TextInput,
} from '../../components/primitives.jsx';
import {
  KpiHeroBand,
  StatusDonutMini,
  InlineScoreCell,
  bandTone,
  KpiBandSkeleton,
  BrandedEmptyState,
  StaggerReveal,
} from '../../components/index.js';
import './admin.css';
import './adminConfig.redesign.css';

const CONFIG_TABS = [
  { to: '/admin/custom-fields', label: 'Custom fields' },
  { to: '/admin/forms', label: 'Form builder' },
  { to: '/admin/rubrics', label: 'Rubrics & LOEs' },
  { to: '/admin/report-templates', label: 'Report registry' },
];

const CONFIG_PAGE_BY_PATH = Object.freeze({
  '/admin/custom-fields': {
    title: 'Configuration',
    subtitle: 'Custom fields, forms, rubrics, and LOE metadata for DHA PRIME.',
    headerTitle: 'Configuration',
    headerSubtitle:
      'Show DHA leadership that PRIME can add fields, govern forms, tune rubrics, and maintain LOE metadata without a fragile code fork.',
    heroStatus: 'Custom Fields',
  },
  '/admin/forms': {
    title: 'Form Builder',
    subtitle: 'Versioned configuration of intake, closeout, and UAT form surfaces.',
    headerTitle: 'Form Builder',
    headerSubtitle:
      'Version intake, closeout, and UAT form structures without leaving the governed PRIME admin shell.',
    heroStatus: 'Form Builder',
  },
  '/admin/rubrics': {
    title: 'Rubric Admin',
    subtitle: 'Weighted scoring criteria and approval-gated rubric activation for DHA PRIME.',
    headerTitle: 'Rubric Admin',
    headerSubtitle:
      'Tune weighted scoring and LOE-aligned review models while preserving governed activation history.',
    heroStatus: 'Rubrics & LOEs',
  },
  '/admin/report-templates': {
    title: 'Report Registry',
    subtitle: 'Governed report-template metadata for briefing, reporting, and export workflows.',
    headerTitle: 'Report Registry',
    headerSubtitle:
      'Govern decks, reports, and export-ready templates from one audited registry for DHA PRIME.',
    heroStatus: 'Report Registry',
  },
  '/admin/config/report-registry': {
    title: 'Report Registry',
    subtitle: 'Governed report-template metadata for briefing, reporting, and export workflows.',
    headerTitle: 'Report Registry',
    headerSubtitle:
      'Govern decks, reports, and export-ready templates from one audited registry for DHA PRIME.',
    heroStatus: 'Report Registry',
  },
});

const ENTITY_OPTIONS = [
  { value: 'intake_request', label: 'Intake Request' },
  { value: 'project', label: 'Project' },
  { value: 'risk', label: 'Risk' },
  { value: 'action_item', label: 'Action Item' },
  { value: 'artifact', label: 'Artifact' },
];

const FIELD_TYPE_OPTIONS = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Textarea' },
  { value: 'long_text', label: 'Long text' },
  { value: 'number', label: 'Number' },
  { value: 'select', label: 'Select' },
  { value: 'multi_select', label: 'Multi-select' },
  { value: 'date', label: 'Date' },
];
const REPORT_TEMPLATE_FORMAT_OPTIONS = [
  { value: 'pdf', label: 'PDF' },
  { value: 'docx', label: 'DOCX' },
  { value: 'html', label: 'HTML' },
  { value: 'pptx', label: 'PPTX' },
  { value: 'md', label: 'Markdown' },
];
const REPORT_TEMPLATE_SENSITIVITY_OPTIONS = [
  { value: 'Public', label: 'Public' },
  { value: 'Internal', label: 'Internal' },
  { value: 'CUI', label: 'CUI' },
];
const EMPTY_ITEMS = Object.freeze([]);

// Human-label lookups so the registry never shows raw enums. Built from the same
// option lists the composer uses, with a startCase fallback for any value that is
// not in the list (keeps the fieldKey itself as a separate technical reference).
const ENTITY_LABELS = Object.freeze(
  Object.fromEntries(ENTITY_OPTIONS.map((option) => [option.value, option.label])),
);
const FIELD_TYPE_LABELS = Object.freeze(
  Object.fromEntries(FIELD_TYPE_OPTIONS.map((option) => [option.value, option.label])),
);

function entityTypeLabel(value) {
  return ENTITY_LABELS[value] || startCase(value);
}

function fieldTypeLabel(value) {
  return FIELD_TYPE_LABELS[value] || startCase(value);
}

// Map a mutation failure to plain, trustworthy language. Root cause of the
// original silent-403 bug: submitCustomField never read the error at all, so a
// forbidden create left a dead button with no feedback.
function friendlyConfigError(error) {
  const status = error?.status;
  if (status === 403) {
    return 'You do not have permission to create custom fields. Custom fields are configured by System Administrators.';
  }
  if (status === 400 || status === 422) {
    return error?.message || 'PRIME could not save this custom field. Check the details and try again.';
  }
  if (!status) {
    return 'Could not reach PRIME. Check your connection and try again.';
  }
  return error?.message || 'PRIME could not save this custom field. Please try again.';
}

const BLANK_DRAFT_FIELD = Object.freeze({
  label: '',
  fieldKey: '',
  entityType: 'project',
  fieldType: 'text',
  required: false,
  dashboardEnabled: false,
  publicStatusEnabled: false,
  reportingEnabled: true,
  exportEnabled: false,
  aiContextEnabled: false,
  editable: true,
  // F1: when true the composer stamps classification 'CUI' and the server's
  // validateCustomFieldDefinition forces the masking defaults (redactionMode
  // 'mask', dashboards/public/AI off, human approval for AI use). Default off.
  containsSensitiveData: false,
  classification: null,
});

function countWhere(items, predicate) {
  return items.filter(predicate).length;
}

function startCase(value) {
  return String(value || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function defaultFieldType(fieldKey) {
  switch (fieldKey) {
    case 'primaryLoeId':
      return 'select';
    case 'problemStatement':
    case 'requestSummary':
    case 'missionImpact':
      return 'textarea';
    default:
      return 'text';
  }
}

function cloneValue(value) {
  return JSON.parse(JSON.stringify(value));
}

function upsertCollectionItem(items = [], nextItem) {
  const list = Array.isArray(items) ? [...items] : [];
  const index = list.findIndex((item) => item?.id === nextItem?.id);
  if (index === -1) return [nextItem, ...list];
  list[index] = nextItem;
  return list;
}

function normalizeFormField(field = {}, fieldIndex = 0) {
  return {
    fieldKey: field.fieldKey || '',
    label: field.label || startCase(field.fieldKey || `Field ${fieldIndex + 1}`),
    fieldType: field.fieldType || defaultFieldType(field.fieldKey),
    required: field.required === true,
    displayOrder: Number(field.displayOrder || fieldIndex + 1),
    placeholder: field.placeholder || '',
    options: Array.isArray(field.options) ? cloneValue(field.options) : [],
  };
}

function normalizeFormSection(section = {}, sectionIndex = 0) {
  return {
    id: section.id || `SEC-CUSTOM-${sectionIndex + 1}`,
    label: section.label || `Section ${sectionIndex + 1}`,
    fields: (Array.isArray(section.fields) ? section.fields : [])
      .map((field, fieldIndex) => normalizeFormField(field, fieldIndex))
      .sort((left, right) => left.displayOrder - right.displayOrder),
  };
}

function normalizeEditableForm(form) {
  if (!form) return null;
  return {
    ...cloneValue(form),
    sections: (Array.isArray(form.sections) ? form.sections : []).map((section, sectionIndex) =>
      normalizeFormSection(section, sectionIndex),
    ),
  };
}

function buildBlankSection(sectionCount) {
  return {
    id: `SEC-CUSTOM-${sectionCount + 1}`,
    label: `New Section ${sectionCount + 1}`,
    fields: [],
  };
}

function buildBlankField(fieldCount) {
  return {
    fieldKey: '',
    label: '',
    fieldType: 'text',
    required: false,
    displayOrder: fieldCount + 1,
    placeholder: '',
    options: [],
  };
}

function sortFields(fields = []) {
  return [...fields]
    .map((field, index) => ({
      ...field,
      displayOrder: Number(field.displayOrder || index + 1),
    }))
    .sort((left, right) => left.displayOrder - right.displayOrder)
    .map((field, index) => ({
      ...field,
      displayOrder: index + 1,
    }));
}

function validateEditableForm(form) {
  const issues = [];
  if (!form) return ['Form selection'];
  (form.sections || []).forEach((section, sectionIndex) => {
    if (!String(section.label || '').trim()) {
      issues.push(`section ${sectionIndex + 1} label`);
    }
    if (!Array.isArray(section.fields) || section.fields.length === 0) {
      issues.push(`section ${sectionIndex + 1} fields`);
      return;
    }
    section.fields.forEach((field, fieldIndex) => {
      if (!String(field.fieldKey || '').trim()) {
        issues.push(`section ${sectionIndex + 1} field ${fieldIndex + 1} key`);
      }
      if (!String(field.label || '').trim()) {
        issues.push(`section ${sectionIndex + 1} field ${fieldIndex + 1} label`);
      }
    });
  });
  return issues;
}

function slugToken(value, fallback = 'CUSTOM') {
  const normalized = String(value || '')
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toUpperCase();
  return normalized || fallback;
}

function buildRubricCriterionId(label, criterionIndex) {
  return `CRIT-${slugToken(label, `CUSTOM-${criterionIndex + 1}`)}`;
}

function normalizeRubricCriterion(criterion = {}) {
  return {
    id: criterion.id || '',
    label: criterion.label || '',
    weight: Number(criterion.weight ?? 0),
    description: criterion.description || '',
  };
}

function normalizeEditableRubric(rubric) {
  if (!rubric) return null;
  return {
    ...cloneValue(rubric),
    criteria: (Array.isArray(rubric.criteria) ? rubric.criteria : []).map(
      (criterion, criterionIndex) => normalizeRubricCriterion(criterion, criterionIndex),
    ),
  };
}

function buildBlankRubricCriterion() {
  return {
    id: '',
    label: '',
    weight: 0,
    description: '',
  };
}

function validateEditableRubric(rubric) {
  const issues = [];
  if (!rubric) return ['Rubric selection'];
  if (!String(rubric.name || '').trim()) {
    issues.push('rubric name');
  }
  if (!Array.isArray(rubric.criteria) || rubric.criteria.length === 0) {
    issues.push('at least one criterion');
    return issues;
  }
  rubric.criteria.forEach((criterion, criterionIndex) => {
    if (!String(criterion.label || '').trim()) {
      issues.push(`criterion ${criterionIndex + 1} label`);
    }
    if (!Number.isFinite(Number(criterion.weight)) || Number(criterion.weight) <= 0) {
      issues.push(`criterion ${criterionIndex + 1} weight`);
    }
  });
  return issues;
}

function normalizeEditableReportTemplate(template) {
  if (!template) return null;
  return {
    ...cloneValue(template),
    description: template.description || '',
    ownerUserId: template.ownerUserId || '',
    sensitivity: template.sensitivity || 'CUI',
    outputFormats: Array.isArray(template.outputFormats) ? [...template.outputFormats] : ['pdf'],
    requiredSections: Array.isArray(template.requiredSections)
      ? [...template.requiredSections]
      : [],
    requiredRoles: Array.isArray(template.requiredRoles) ? [...template.requiredRoles] : [],
    allowedScopes: Array.isArray(template.allowedScopes) ? [...template.allowedScopes] : [],
    requiredDataSources: Array.isArray(template.requiredDataSources)
      ? [...template.requiredDataSources]
      : [],
    aiAllowed: template.aiAllowed !== false,
    humanReviewRequired: template.humanReviewRequired !== false,
  };
}

function validateEditableReportTemplate(template) {
  const issues = [];
  if (!template) return ['Report template selection'];
  if (!String(template.name || '').trim()) {
    issues.push('template name');
  }
  if (!String(template.description || '').trim()) {
    issues.push('template description');
  }
  if (!String(template.productType || '').trim()) {
    issues.push('product type');
  }
  if (!Array.isArray(template.requiredSections) || template.requiredSections.length === 0) {
    issues.push('required sections');
  }
  return issues;
}

export function AdminConfigurationPage() {
  const location = useLocation();
  const { roles = [] } = useCurrentUser();
  // Route access to /admin/custom-fields is CONFIG_VIEW (Data Steward + System
  // Admin), but the POST is CONFIG_EDIT (System Admin only). Align the UI to the
  // server so a viewer never sees a live-but-doomed composer.
  const canEditConfig = rbac.hasAnyRole(roles, [rbac.ROLE_IDS.SYSTEM_ADMIN]);
  const customFieldsRes = useApi('/admin/custom-fields', { query: { limit: 200 } });
  const formsRes = useApi('/admin/forms', { query: { limit: 100 } });
  const rubricsRes = useApi('/admin/rubrics', { query: { limit: 100 } });
  const loesRes = useApi('/admin/loes', { query: { limit: 100 } });
  const reportTemplatesRes = useApi('/admin/report-templates', { query: { limit: 100 } });
  const createFieldMutation = useMutation();
  const stateChangeMutation = useMutation();

  const [draftField, setDraftField] = useState({ ...BLANK_DRAFT_FIELD });
  const [addToIntakeForm, setAddToIntakeForm] = useState(true);
  const [customFieldNotice, setCustomFieldNotice] = useState(null);
  const [selectedFormId, setSelectedFormId] = useState('');
  const [editableForm, setEditableForm] = useState(null);
  const [formBuilderMessage, setFormBuilderMessage] = useState('');
  const [formBuilderError, setFormBuilderError] = useState('');
  const [selectedRubricId, setSelectedRubricId] = useState('');
  const [editableRubric, setEditableRubric] = useState(null);
  const [rubricBuilderMessage, setRubricBuilderMessage] = useState('');
  const [rubricBuilderError, setRubricBuilderError] = useState('');
  const [selectedReportTemplateId, setSelectedReportTemplateId] = useState('');
  const [editableReportTemplate, setEditableReportTemplate] = useState(null);
  const [reportTemplateBuilderMessage, setReportTemplateBuilderMessage] = useState('');
  const [reportTemplateBuilderError, setReportTemplateBuilderError] = useState('');
  const [reportTemplateRegistry, setReportTemplateRegistry] = useState([]);

  const pageConfig =
    CONFIG_PAGE_BY_PATH[location.pathname] || CONFIG_PAGE_BY_PATH['/admin/custom-fields'];

  usePageMetadata({
    title: pageConfig.title,
    subtitle: pageConfig.subtitle,
  });

  function resetDraftField() {
    setDraftField({ ...BLANK_DRAFT_FIELD });
    setAddToIntakeForm(true);
  }

  // Resolve the active Project Intake Form (PIF) explicitly. Multiple intake_request
  // forms can be active at once (PIF + Request for Support), and a bare "first active
  // intake form" finder can pick the RFS. The demo story places composer fields on the
  // PIF, so prefer formType 'pif', then the canonical PIF id, then any active intake
  // form as a last resort.
  function findActivePifForm() {
    const activeIntakeForms = forms.filter(
      (form) =>
        String(form.entityType || '').toLowerCase() === 'intake_request' &&
        String(form.status || '').toLowerCase() === 'active',
    );
    return (
      activeIntakeForms.find((form) => String(form.formType || '').toLowerCase() === 'pif') ||
      activeIntakeForms.find((form) => String(form.id || '') === 'FORM-PIF-OWHA-V1') ||
      activeIntakeForms[0] ||
      null
    );
  }

  // Auto-place a just-created intake_request field onto the active PIF form using
  // the exact fieldKey we created (no free-text re-typing). Reuses the existing
  // PATCH /admin/forms/:id — no new endpoint. Returns whether a placement PATCH
  // ran so the success banner can be specific.
  async function placeFieldOnActiveIntakeForm(savedField) {
    const activeIntakeForm = findActivePifForm();
    if (!activeIntakeForm) {
      return { added: false, reason: 'no_active_form' };
    }
    const sections = Array.isArray(activeIntakeForm.sections) ? activeIntakeForm.sections : [];
    const alreadyPlaced = sections.some((section) =>
      (section.fields || []).some((field) => field.fieldKey === savedField.fieldKey),
    );
    if (alreadyPlaced) {
      return { added: false, reason: 'already_placed' };
    }
    const targetIndex = sections.length > 0 ? 0 : -1;
    const nextSections = (targetIndex === -1
      ? [{ id: 'SEC-DIVISION-FIELDS', label: 'Division fields', fields: [] }]
      : sections
    ).map((section, index) => {
      const normalizedFields = sortFields(section.fields || []).map((field) => ({
        fieldKey: field.fieldKey,
        label: field.label,
        fieldType: field.fieldType,
        required: field.required === true,
        displayOrder: field.displayOrder,
        placeholder: field.placeholder || '',
        options: Array.isArray(field.options) ? field.options : [],
      }));
      if (index !== (targetIndex === -1 ? 0 : targetIndex)) {
        return { id: section.id, label: section.label, fields: normalizedFields };
      }
      const maxOrder = normalizedFields.reduce(
        (max, field) => Math.max(max, Number(field.displayOrder || 0)),
        0,
      );
      return {
        id: section.id,
        label: section.label,
        fields: [
          ...normalizedFields,
          {
            fieldKey: savedField.fieldKey,
            label: savedField.label,
            fieldType: savedField.fieldType || 'text',
            required: savedField.required === true,
            displayOrder: maxOrder + 1,
            placeholder: '',
            options: [],
          },
        ],
      };
    });
    const response = await api.patch(`/admin/forms/${activeIntakeForm.id}`, {
      sections: nextSections,
    });
    const savedForm = normalizeEditableForm(response?.data || response);
    if (savedForm) {
      formsRes.setData((current) => {
        if (!current?.data) return current;
        return {
          ...current,
          data: current.data.map((form) => (form.id === savedForm.id ? savedForm : form)),
        };
      });
    }
    return { added: true, form: savedForm };
  }

  async function submitCustomField(event) {
    event.preventDefault();
    setCustomFieldNotice(null);
    let savedField;
    try {
      const response = await createFieldMutation.run(() =>
        api.post('/admin/custom-fields', {
          ...draftField,
          reportingEnabled: draftField.reportingEnabled !== false,
        }),
      );
      savedField = response?.data || response;
    } catch (error) {
      setCustomFieldNotice({ tone: 'error', message: friendlyConfigError(error) });
      return;
    }
    customFieldsRes.setData((current) => {
      if (!current?.data) return current;
      return {
        ...current,
        data: [savedField, ...current.data],
      };
    });

    const createdEntityType = savedField?.entityType ?? draftField.entityType;
    let addedToForm = false;
    if (createdEntityType === 'intake_request' && addToIntakeForm) {
      try {
        const placement = await placeFieldOnActiveIntakeForm(savedField);
        addedToForm = placement?.added === true;
      } catch (placementError) {
        setCustomFieldNotice({
          tone: 'error',
          message: `Custom field "${savedField.label}" was created, but PRIME could not add it to the Project Intake Form (PIF): ${friendlyConfigError(
            placementError,
          )}`,
        });
        resetDraftField();
        return;
      }
    }

    setCustomFieldNotice({
      tone: 'success',
      message: `Custom field "${savedField.label}" created${
        addedToForm ? ' and added to the Project Intake Form (PIF)' : ''
      }.`,
    });
    resetDraftField();
  }

  async function activateForm(formId) {
    await stateChangeMutation.run(() => api.post(`/admin/forms/${formId}/activate`, {}));
  }

  async function activateRubric(rubricId) {
    await stateChangeMutation.run(() => api.post(`/admin/rubrics/${rubricId}/activate`, {}));
  }

  async function activateReportTemplate(templateId) {
    const response = await stateChangeMutation.run(() =>
      api.post(`/admin/report-templates/${templateId}/activate`, {}),
    );
    const activatedTemplate = response?.data;
    if (activatedTemplate) {
      reportTemplatesRes.setData((current) => ({
        ...(current || {}),
        data: upsertCollectionItem(current?.data, activatedTemplate),
        page: current?.page
          ? {
              ...current.page,
              total: Math.max(Number(current.page.total || 0), reportTemplates.length),
            }
          : { limit: 100, offset: 0, total: reportTemplates.length },
      }));
      setReportTemplateRegistry((current) => upsertCollectionItem(current, activatedTemplate));
      if (selectedReportTemplateId === activatedTemplate.id) {
        setEditableReportTemplate(normalizeEditableReportTemplate(activatedTemplate));
      }
      await reportTemplatesRes.refresh();
      setReportTemplateBuilderError('');
      setReportTemplateBuilderMessage(
        `Activated report template version ${activatedTemplate.version || 1}.`,
      );
    }
  }

  const customFields = customFieldsRes.data?.data ?? EMPTY_ITEMS;
  const forms = formsRes.data?.data ?? EMPTY_ITEMS;
  const rubrics = rubricsRes.data?.data ?? EMPTY_ITEMS;
  const loes = loesRes.data?.data ?? EMPTY_ITEMS;
  const reportTemplates = reportTemplatesRes.data?.data ?? EMPTY_ITEMS;

  useEffect(() => {
    setReportTemplateRegistry(reportTemplates);
  }, [reportTemplates]);

  useEffect(() => {
    if (forms.length === 0) {
      setSelectedFormId('');
      setEditableForm(null);
      return;
    }
    const nextFormId = forms.some((form) => form.id === selectedFormId)
      ? selectedFormId
      : forms[0].id;
    const nextForm = forms.find((form) => form.id === nextFormId) || forms[0];
    if (nextFormId !== selectedFormId) {
      setSelectedFormId(nextFormId);
    }
    setEditableForm(normalizeEditableForm(nextForm));
  }, [forms, selectedFormId]);

  useEffect(() => {
    if (rubrics.length === 0) {
      setSelectedRubricId('');
      setEditableRubric(null);
      return;
    }
    const nextRubricId = rubrics.some((rubric) => rubric.id === selectedRubricId)
      ? selectedRubricId
      : rubrics[0].id;
    const nextRubric = rubrics.find((rubric) => rubric.id === nextRubricId) || rubrics[0];
    if (nextRubricId !== selectedRubricId) {
      setSelectedRubricId(nextRubricId);
    }
    setEditableRubric(normalizeEditableRubric(nextRubric));
  }, [rubrics, selectedRubricId]);

  useEffect(() => {
    if (reportTemplateRegistry.length === 0) {
      setSelectedReportTemplateId('');
      setEditableReportTemplate(null);
      return;
    }
    const nextTemplateId = reportTemplateRegistry.some(
      (template) => template.id === selectedReportTemplateId,
    )
      ? selectedReportTemplateId
      : reportTemplateRegistry[0].id;
    const nextTemplate =
      reportTemplateRegistry.find((template) => template.id === nextTemplateId) ||
      reportTemplateRegistry[0];
    if (nextTemplateId !== selectedReportTemplateId) {
      setSelectedReportTemplateId(nextTemplateId);
    }
    setEditableReportTemplate(normalizeEditableReportTemplate(nextTemplate));
  }, [reportTemplateRegistry, selectedReportTemplateId]);

  if (
    customFieldsRes.loading ||
    formsRes.loading ||
    rubricsRes.loading ||
    loesRes.loading ||
    reportTemplatesRes.loading
  ) {
    return (
      <>
        <PageHeader title="Configuration" />
        <KpiBandSkeleton count={5} />
        <LoadingSkeleton rows={7} />
      </>
    );
  }
  if (customFieldsRes.error) return <ErrorState error={customFieldsRes.error} />;
  if (formsRes.error) return <ErrorState error={formsRes.error} />;
  if (rubricsRes.error) return <ErrorState error={rubricsRes.error} />;
  if (loesRes.error) return <ErrorState error={loesRes.error} />;
  if (reportTemplatesRes.error) return <ErrorState error={reportTemplatesRes.error} />;

  // ----- Governance rollups derived from already-fetched config data -----
  const activeFieldCount = countWhere(customFields, (item) => item.status === 'active');
  const draftFormCount = countWhere(forms, (item) => item.status === 'draft');
  const activeRubricCount = countWhere(rubrics, (item) => item.status === 'active');
  const inactiveLoeCount = countWhere(loes, (item) => item.status === 'inactive');
  const activeTemplateCount = countWhere(reportTemplates, (item) => item.status === 'active');

  // Honest status distributions: every record lands in exactly one bucket.
  function buildStatusSegments(items) {
    const counts = new Map();
    for (const item of items) {
      const raw = String(item.status || 'active').toLowerCase();
      const tone =
        raw === 'active'
          ? 'sage'
          : raw === 'draft'
            ? 'amber'
            : raw === 'inactive' || raw === 'archived' || raw === 'retired'
              ? 'brick'
              : 'slate';
      const label = startCase(raw) || 'Active';
      const prev = counts.get(label) || { label, count: 0, tone };
      prev.count += 1;
      counts.set(label, prev);
    }
    return [...counts.values()];
  }

  const fieldStatusSegments = buildStatusSegments(customFields);
  const templateStatusSegments = buildStatusSegments(reportTemplates);

  const kpiItems = [
    {
      id: 'active-fields',
      label: 'Active fields',
      value: activeFieldCount,
      status: activeFieldCount > 0 ? 'sage' : 'slate',
      statusLabel: activeFieldCount > 0 ? 'Active' : 'None',
      footer: `${customFields.length} configured`,
    },
    {
      id: 'draft-forms',
      label: 'Draft forms',
      value: draftFormCount,
      status: draftFormCount > 0 ? 'amber' : 'slate',
      statusLabel: draftFormCount > 0 ? 'Awaiting activation' : 'Clear',
      footer: `${forms.length} form${forms.length === 1 ? '' : 's'} total`,
    },
    {
      id: 'active-rubrics',
      label: 'Active rubrics',
      value: activeRubricCount,
      status: activeRubricCount > 0 ? 'sage' : 'slate',
      statusLabel: activeRubricCount > 0 ? 'Scoring' : 'None',
      footer: `${rubrics.length} rubric${rubrics.length === 1 ? '' : 's'} total`,
    },
    {
      id: 'inactive-loes',
      label: 'Inactive LOEs',
      value: inactiveLoeCount,
      status: inactiveLoeCount > 0 ? 'amber' : 'slate',
      statusLabel: inactiveLoeCount > 0 ? 'History only' : 'All active',
      footer: `${loes.length} line${loes.length === 1 ? '' : 's'} of effort`,
    },
    {
      id: 'active-templates',
      label: 'Active templates',
      value: activeTemplateCount,
      status: activeTemplateCount > 0 ? 'sage' : 'slate',
      statusLabel: activeTemplateCount > 0 ? 'Governed' : 'None',
      footer: `${reportTemplates.length} registered`,
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Admin & Compliance"
        title={pageConfig.headerTitle}
        subtitle={pageConfig.headerSubtitle}
      />

      <nav className="admin-config-tabs" aria-label="Admin configuration sections">
        {CONFIG_TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            className={({ isActive }) =>
              `admin-config-tab${isActive || location.pathname === tab.to ? ' active' : ''}`
            }
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>

      <section className="card admin-config-hero">
        <div>
          <div className="eyebrow">Configurability posture</div>
          <h2 className="card-title">No-code governance for OWHA-specific delivery</h2>
          <p className="muted">
            These controls let administrators extend intake, scoring, and LOE metadata while keeping
            version history, audit evidence, and conservative visibility defaults in place.
          </p>
        </div>
        <StatusChip status="active">{pageConfig.heroStatus}</StatusChip>
      </section>

      <KpiHeroBand
        className="admin-config-rollups"
        eyebrow="Configuration posture"
        asOf={customFieldsRes.loadedAt}
        columns={5}
        items={kpiItems}
      />

      {customFields.length > 0 || reportTemplates.length > 0 ? (
        <section className="card admin-config-distribution-grid">
          {customFields.length > 0 ? (
            <div className="admin-config-distribution-panel">
              <StatusDonutMini
                segments={fieldStatusSegments}
                totalLabel="Fields"
                ariaLabel="Custom field status distribution"
              />
              <div>
                <div className="eyebrow">Custom field status</div>
                <p className="muted small">
                  Governed visibility of every configured field across active, draft, and retired
                  states.
                </p>
              </div>
            </div>
          ) : null}
          {reportTemplates.length > 0 ? (
            <div className="admin-config-distribution-panel">
              <StatusDonutMini
                segments={templateStatusSegments}
                totalLabel="Templates"
                ariaLabel="Report template status distribution"
              />
              <div>
                <div className="eyebrow">Report template status</div>
                <p className="muted small">
                  Audited export registry split across active, draft, and archived template
                  versions.
                </p>
              </div>
            </div>
          ) : null}
        </section>
      ) : null}

      <section className="bento admin-bento">
        <div className="span-5 card">
          <div className="eyebrow">Custom field composer</div>
          <h2 className="card-title">Add a governed custom field</h2>
          {canEditConfig ? (
            <form className="admin-config-create-form" onSubmit={submitCustomField}>
            <div className="admin-config-create-grid">
              <FormField label="Field label" htmlFor="config-field-label" required>
                <TextInput
                  id="config-field-label"
                  value={draftField.label}
                  onChange={(value) => setDraftField((current) => ({ ...current, label: value }))}
                />
              </FormField>
              <FormField label="Field key" htmlFor="config-field-key" required>
                <TextInput
                  id="config-field-key"
                  value={draftField.fieldKey}
                  onChange={(value) =>
                    setDraftField((current) => ({ ...current, fieldKey: value }))
                  }
                />
              </FormField>
              <FormField label="Entity type" htmlFor="config-field-entity" required>
                <Select
                  id="config-field-entity"
                  value={draftField.entityType}
                  onChange={(value) =>
                    setDraftField((current) => ({ ...current, entityType: value }))
                  }
                  options={ENTITY_OPTIONS}
                />
              </FormField>
              <FormField label="Field type" htmlFor="config-field-type" required>
                <Select
                  id="config-field-type"
                  value={draftField.fieldType}
                  onChange={(value) =>
                    setDraftField((current) => ({ ...current, fieldType: value }))
                  }
                  options={FIELD_TYPE_OPTIONS}
                />
              </FormField>
              <FormField label="Required" htmlFor="config-field-required">
                <label className="admin-config-checkbox" htmlFor="config-field-required">
                  <input
                    id="config-field-required"
                    type="checkbox"
                    checked={draftField.required === true}
                    onChange={(event) =>
                      setDraftField((current) => ({
                        ...current,
                        required: event.target.checked,
                      }))
                    }
                  />
                  <span>Required field</span>
                </label>
              </FormField>
              <FormField
                label="Visibility"
                htmlFor="config-field-dashboard-enabled"
                hint="Choose whether PRIME should surface the field in dashboard workspace rollups."
              >
                <div className="admin-config-checkbox-stack">
                  <label className="admin-config-checkbox" htmlFor="config-field-dashboard-enabled">
                    <input
                      id="config-field-dashboard-enabled"
                      type="checkbox"
                      checked={draftField.dashboardEnabled === true}
                      onChange={(event) =>
                        setDraftField((current) => ({
                          ...current,
                          dashboardEnabled: event.target.checked,
                        }))
                      }
                    />
                    <span>Include in dashboards</span>
                  </label>
                  <label
                    className="admin-config-checkbox"
                    htmlFor="config-field-public-status-enabled"
                  >
                    <input
                      id="config-field-public-status-enabled"
                      type="checkbox"
                      checked={draftField.publicStatusEnabled === true}
                      onChange={(event) =>
                        setDraftField((current) => ({
                          ...current,
                          publicStatusEnabled: event.target.checked,
                        }))
                      }
                    />
                    <span>Visible in public status</span>
                  </label>
                </div>
              </FormField>
              <FormField
                label="Exports & AI"
                htmlFor="config-field-export-enabled"
                hint="Control whether generated products and AI workflows can use the field."
              >
                <div className="admin-config-checkbox-stack">
                  <label className="admin-config-checkbox" htmlFor="config-field-export-enabled">
                    <input
                      id="config-field-export-enabled"
                      type="checkbox"
                      checked={draftField.exportEnabled === true}
                      onChange={(event) =>
                        setDraftField((current) => ({
                          ...current,
                          exportEnabled: event.target.checked,
                        }))
                      }
                    />
                    <span>Allow exports</span>
                  </label>
                  <label
                    className="admin-config-checkbox"
                    htmlFor="config-field-ai-context-enabled"
                  >
                    <input
                      id="config-field-ai-context-enabled"
                      type="checkbox"
                      checked={draftField.aiContextEnabled === true}
                      onChange={(event) =>
                        setDraftField((current) => ({
                          ...current,
                          aiContextEnabled: event.target.checked,
                        }))
                      }
                    />
                    <span>Allow AI context</span>
                  </label>
                  <label
                    className="admin-config-checkbox"
                    htmlFor="config-field-reporting-enabled"
                  >
                    <input
                      id="config-field-reporting-enabled"
                      type="checkbox"
                      checked={draftField.reportingEnabled !== false}
                      onChange={(event) =>
                        setDraftField((current) => ({
                          ...current,
                          reportingEnabled: event.target.checked,
                        }))
                      }
                    />
                    <span>Include in reports</span>
                  </label>
                </div>
              </FormField>
              <FormField
                label="Editability"
                htmlFor="config-field-editable"
                hint="Disable request-side editing for governance-controlled or reviewer-owned fields."
              >
                <label className="admin-config-checkbox" htmlFor="config-field-editable">
                  <input
                    id="config-field-editable"
                    type="checkbox"
                    checked={draftField.editable !== false}
                    onChange={(event) =>
                      setDraftField((current) => ({
                        ...current,
                        editable: event.target.checked,
                      }))
                    }
                  />
                  <span>Allow record edits</span>
                </label>
              </FormField>
              <FormField
                label="Data handling"
                htmlFor="config-field-contains-sensitive"
                hint="Sensitive fields are marked CUI, so PRIME masks their values for roles without full access and keeps them out of dashboards, public status, and AI context."
              >
                <label
                  className="admin-config-checkbox"
                  htmlFor="config-field-contains-sensitive"
                >
                  <input
                    id="config-field-contains-sensitive"
                    type="checkbox"
                    checked={draftField.containsSensitiveData === true}
                    onChange={(event) =>
                      setDraftField((current) => ({
                        ...current,
                        containsSensitiveData: event.target.checked,
                        classification: event.target.checked ? 'CUI' : null,
                      }))
                    }
                  />
                  <span>
                    Contains sensitive data (CUI) — values will be masked for roles without full
                    access
                  </span>
                </label>
              </FormField>
              {draftField.entityType === 'intake_request' ? (
                <FormField
                  label="Project Intake Form placement"
                  htmlFor="config-field-add-to-intake-form"
                  hint="PRIME adds the new field to the active Project Intake Form (PIF) so it appears on the request form right away."
                >
                  <label
                    className="admin-config-checkbox"
                    htmlFor="config-field-add-to-intake-form"
                  >
                    <input
                      id="config-field-add-to-intake-form"
                      type="checkbox"
                      checked={addToIntakeForm}
                      onChange={(event) => setAddToIntakeForm(event.target.checked)}
                    />
                    <span>Add to the Project Intake Form (PIF)</span>
                  </label>
                </FormField>
              ) : null}
            </div>
            {customFieldNotice ? (
              <p
                role="alert"
                className={`admin-config-notice admin-config-notice-${customFieldNotice.tone}`}
              >
                {customFieldNotice.message}
              </p>
            ) : null}
            <button className="btn primary" type="submit" disabled={createFieldMutation.running}>
              Create custom field
            </button>
            </form>
          ) : (
            <div className="admin-config-viewonly" role="note">
              <p className="muted">
                Custom fields are configured by System Administrators. You have view access to the
                registry.
              </p>
            </div>
          )}
        </div>

        <div className="span-7 card">
          <div className="eyebrow">Field registry</div>
          <h2 className="card-title">Configured custom fields</h2>
          {customFields.length === 0 ? (
            <BrandedEmptyState
              icon="🧩"
              eyebrow="Field registry"
              title="No custom fields yet"
              description="Create a field to start the governed OWHA configuration path."
            />
          ) : (
            <StaggerReveal as="ul" className="admin-config-list">
              {customFields.map((field) => (
                <li key={field.id}>
                  <div>
                    <strong>{field.label}</strong>
                    <p>
                      {entityTypeLabel(field.entityType)} · {fieldTypeLabel(field.fieldType)}
                    </p>
                    <code
                      className="admin-config-fieldkey"
                      style={{
                        fontFamily: 'var(--font-mono, ui-monospace, monospace)',
                        fontSize: '0.75rem',
                        opacity: 0.85,
                      }}
                    >
                      {field.fieldKey}
                    </code>
                    <div className="admin-config-inline-meta">
                      <span className="chip outline sm">v{field.version || 1}</span>
                      {field.required ? <span className="chip outline sm">Required</span> : null}
                      {field.dashboardEnabled ? (
                        <span className="chip outline sm">Dashboards</span>
                      ) : null}
                      {field.publicStatusEnabled ? (
                        <span className="chip outline sm">Public status</span>
                      ) : null}
                      {field.reportingEnabled ? (
                        <span className="chip outline sm">Reports</span>
                      ) : null}
                      {field.exportEnabled ? (
                        <span className="chip outline sm">Exports</span>
                      ) : null}
                      {field.aiContextEnabled ? (
                        <span className="chip outline sm">AI context</span>
                      ) : null}
                      {field.editable === false ? (
                        <span className="chip outline sm">Read only</span>
                      ) : null}
                    </div>
                  </div>
                  <StatusChip status={field.status || 'active'} />
                </li>
              ))}
            </StaggerReveal>
          )}
        </div>

        <div className="span-6 card">
          <div className="eyebrow">Form builder</div>
          <h2 className="card-title">Versioned forms</h2>
          {forms.length === 0 ? (
            <EmptyState title="No configured forms" />
          ) : (
            <ul className="admin-config-list">
              {forms.map((form) => (
                <li key={form.id}>
                  <div>
                    <strong>{form.name}</strong>
                    <p>
                      {startCase(form.formType)} · {entityTypeLabel(form.entityType)} · v
                      {form.version || 1}
                    </p>
                    <div className="admin-config-inline-meta">
                      <span className="chip outline sm">
                        {(form.sections || []).length} section
                        {(form.sections || []).length === 1 ? '' : 's'}
                      </span>
                      <span className="chip outline sm">
                        {(form.sections || []).reduce(
                          (total, section) => total + (section.fields || []).length,
                          0,
                        )}{' '}
                        field
                        {(form.sections || []).reduce(
                          (total, section) => total + (section.fields || []).length,
                          0,
                        ) === 1
                          ? ''
                          : 's'}
                      </span>
                    </div>
                  </div>
                  <div className="admin-config-actions">
                    <StatusChip status={form.status || 'draft'} />
                    <button
                      type="button"
                      className="btn ghost sm"
                      onClick={() => {
                        setSelectedFormId(form.id);
                        setEditableForm(normalizeEditableForm(form));
                        setFormBuilderError('');
                        setFormBuilderMessage('');
                      }}
                    >
                      Edit structure
                    </button>
                    {form.status !== 'active' ? (
                      <button
                        type="button"
                        className="btn ghost sm"
                        disabled={stateChangeMutation.running}
                        onClick={() => activateForm(form.id)}
                      >
                        Activate form
                      </button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="span-12 card">
          <div className="eyebrow">Governed form structure</div>
          <h2 className="card-title">Configure sections and fields</h2>
          {!editableForm ? (
            <EmptyState
              title="Select a form to configure"
              description="Choose a governed PIF or Request for Support definition to edit sections and fields."
            />
          ) : (
            <div className="admin-form-builder">
              <div className="admin-form-builder-toolbar">
                <FormField label="Form definition" htmlFor="admin-form-selector">
                  <Select
                    id="admin-form-selector"
                    value={selectedFormId}
                    onChange={(value) => {
                      const nextForm = forms.find((form) => form.id === value);
                      setSelectedFormId(value);
                      setEditableForm(normalizeEditableForm(nextForm));
                      setFormBuilderError('');
                      setFormBuilderMessage('');
                    }}
                    options={forms.map((form) => ({
                      value: form.id,
                      label: `${form.name} (v${form.version || 1})`,
                    }))}
                  />
                </FormField>
                <div className="admin-form-builder-toolbar-actions">
                  <button
                    type="button"
                    className="btn ghost"
                    onClick={() => {
                      setEditableForm(
                        normalizeEditableForm(forms.find((form) => form.id === selectedFormId)),
                      );
                      setFormBuilderError('');
                      setFormBuilderMessage('');
                    }}
                  >
                    Reset edits
                  </button>
                  <button
                    type="button"
                    className="btn primary"
                    disabled={stateChangeMutation.running}
                    onClick={async () => {
                      const issues = validateEditableForm(editableForm);
                      if (issues.length > 0) {
                        setFormBuilderError(
                          `Complete the builder before saving: ${issues.join(', ')}.`,
                        );
                        setFormBuilderMessage('');
                        return;
                      }

                      const response = await stateChangeMutation.run(() =>
                        api.patch(`/admin/forms/${editableForm.id}`, {
                          sections: (editableForm.sections || []).map((section) => ({
                            id: section.id,
                            label: section.label,
                            fields: sortFields(section.fields || []).map((field) => ({
                              fieldKey: field.fieldKey,
                              label: field.label,
                              fieldType: field.fieldType,
                              required: field.required === true,
                              displayOrder: field.displayOrder,
                              placeholder: field.placeholder || '',
                              options: Array.isArray(field.options) ? field.options : [],
                            })),
                          })),
                        }),
                      );
                      const savedForm = normalizeEditableForm(response?.data || response);
                      setEditableForm(savedForm);
                      setFormBuilderError('');
                      setFormBuilderMessage(
                        `Saved form definition version ${savedForm?.version || editableForm.version || 1}.`,
                      );
                      formsRes.setData((current) => {
                        if (!current?.data) return current;
                        return {
                          ...current,
                          data: current.data.map((form) =>
                            form.id === savedForm.id ? savedForm : form,
                          ),
                        };
                      });
                    }}
                  >
                    Save form changes
                  </button>
                </div>
              </div>

              <div className="admin-config-inline-meta">
                <span className="chip outline sm">{editableForm.formType}</span>
                <span className="chip outline sm">{editableForm.entityType}</span>
                <span className="chip outline sm">v{editableForm.version || 1}</span>
                <StatusChip status={editableForm.status || 'draft'} />
              </div>

              <div className="admin-form-section-list">
                {(editableForm.sections || []).map((section, sectionIndex) => (
                  <section
                    key={`${section.id}-${sectionIndex}`}
                    className="admin-form-section-card"
                    data-testid={`admin-form-section-${sectionIndex}`}
                  >
                    <div className="admin-form-section-head">
                      <FormField
                        label="Section label"
                        htmlFor={`admin-form-section-label-${sectionIndex}`}
                      >
                        <TextInput
                          id={`admin-form-section-label-${sectionIndex}`}
                          value={section.label}
                          onChange={(value) => {
                            setEditableForm((current) => ({
                              ...current,
                              sections: current.sections.map((item, itemIndex) =>
                                itemIndex === sectionIndex ? { ...item, label: value } : item,
                              ),
                            }));
                            setFormBuilderError('');
                            setFormBuilderMessage('');
                          }}
                        />
                      </FormField>
                      <div className="admin-config-actions">
                        <button
                          type="button"
                          className="btn ghost sm"
                          onClick={() => {
                            setEditableForm((current) => ({
                              ...current,
                              sections: current.sections.map((item, itemIndex) =>
                                itemIndex === sectionIndex
                                  ? {
                                      ...item,
                                      fields: [
                                        ...sortFields(item.fields || []),
                                        buildBlankField((item.fields || []).length),
                                      ],
                                    }
                                  : item,
                              ),
                            }));
                            setFormBuilderError('');
                            setFormBuilderMessage('');
                          }}
                        >
                          Add field
                        </button>
                        <button
                          type="button"
                          className="btn ghost sm"
                          disabled={(editableForm.sections || []).length === 1}
                          onClick={() => {
                            setEditableForm((current) => ({
                              ...current,
                              sections: current.sections.filter(
                                (_item, itemIndex) => itemIndex !== sectionIndex,
                              ),
                            }));
                            setFormBuilderError('');
                            setFormBuilderMessage('');
                          }}
                        >
                          Remove section
                        </button>
                      </div>
                    </div>

                    <div className="admin-form-field-list">
                      {(section.fields || []).map((field, fieldIndex) => (
                        <div
                          key={`${field.fieldKey || 'new'}-${fieldIndex}`}
                          className="admin-form-field-row"
                          data-testid={`admin-form-field-${sectionIndex}-${fieldIndex}`}
                        >
                          <FormField
                            label="Field key"
                            htmlFor={`admin-form-field-key-${sectionIndex}-${fieldIndex}`}
                          >
                            <TextInput
                              id={`admin-form-field-key-${sectionIndex}-${fieldIndex}`}
                              value={field.fieldKey}
                              onChange={(value) => {
                                setEditableForm((current) => ({
                                  ...current,
                                  sections: current.sections.map((item, itemIndex) =>
                                    itemIndex === sectionIndex
                                      ? {
                                          ...item,
                                          fields: sortFields(item.fields || []).map(
                                            (fieldItem, nestedIndex) =>
                                              nestedIndex === fieldIndex
                                                ? { ...fieldItem, fieldKey: value }
                                                : fieldItem,
                                          ),
                                        }
                                      : item,
                                  ),
                                }));
                                setFormBuilderError('');
                                setFormBuilderMessage('');
                              }}
                            />
                          </FormField>
                          <FormField
                            label="Field label"
                            htmlFor={`admin-form-field-label-${sectionIndex}-${fieldIndex}`}
                          >
                            <TextInput
                              id={`admin-form-field-label-${sectionIndex}-${fieldIndex}`}
                              value={field.label}
                              onChange={(value) => {
                                setEditableForm((current) => ({
                                  ...current,
                                  sections: current.sections.map((item, itemIndex) =>
                                    itemIndex === sectionIndex
                                      ? {
                                          ...item,
                                          fields: sortFields(item.fields || []).map(
                                            (fieldItem, nestedIndex) =>
                                              nestedIndex === fieldIndex
                                                ? { ...fieldItem, label: value }
                                                : fieldItem,
                                          ),
                                        }
                                      : item,
                                  ),
                                }));
                                setFormBuilderError('');
                                setFormBuilderMessage('');
                              }}
                            />
                          </FormField>
                          <FormField
                            label="Field type"
                            htmlFor={`admin-form-field-type-${sectionIndex}-${fieldIndex}`}
                          >
                            <Select
                              id={`admin-form-field-type-${sectionIndex}-${fieldIndex}`}
                              value={field.fieldType}
                              onChange={(value) => {
                                setEditableForm((current) => ({
                                  ...current,
                                  sections: current.sections.map((item, itemIndex) =>
                                    itemIndex === sectionIndex
                                      ? {
                                          ...item,
                                          fields: sortFields(item.fields || []).map(
                                            (fieldItem, nestedIndex) =>
                                              nestedIndex === fieldIndex
                                                ? { ...fieldItem, fieldType: value }
                                                : fieldItem,
                                          ),
                                        }
                                      : item,
                                  ),
                                }));
                                setFormBuilderError('');
                                setFormBuilderMessage('');
                              }}
                              options={FIELD_TYPE_OPTIONS}
                            />
                          </FormField>
                          <FormField
                            label="Required"
                            htmlFor={`admin-form-field-required-${sectionIndex}-${fieldIndex}`}
                          >
                            <label
                              className="admin-config-checkbox"
                              htmlFor={`admin-form-field-required-${sectionIndex}-${fieldIndex}`}
                            >
                              <input
                                id={`admin-form-field-required-${sectionIndex}-${fieldIndex}`}
                                aria-label="Required field"
                                type="checkbox"
                                checked={field.required === true}
                                onChange={(event) => {
                                  setEditableForm((current) => ({
                                    ...current,
                                    sections: current.sections.map((item, itemIndex) =>
                                      itemIndex === sectionIndex
                                        ? {
                                            ...item,
                                            fields: sortFields(item.fields || []).map(
                                              (fieldItem, nestedIndex) =>
                                                nestedIndex === fieldIndex
                                                  ? {
                                                      ...fieldItem,
                                                      required: event.target.checked,
                                                    }
                                                  : fieldItem,
                                            ),
                                          }
                                        : item,
                                    ),
                                  }));
                                  setFormBuilderError('');
                                  setFormBuilderMessage('');
                                }}
                              />
                              <span>Required field</span>
                            </label>
                          </FormField>
                          <div className="admin-form-field-actions">
                            <button
                              type="button"
                              className="btn ghost sm"
                              disabled={(section.fields || []).length === 1}
                              onClick={() => {
                                setEditableForm((current) => ({
                                  ...current,
                                  sections: current.sections.map((item, itemIndex) =>
                                    itemIndex === sectionIndex
                                      ? {
                                          ...item,
                                          fields: sortFields(item.fields || []).filter(
                                            (_field, nestedIndex) => nestedIndex !== fieldIndex,
                                          ),
                                        }
                                      : item,
                                  ),
                                }));
                                setFormBuilderError('');
                                setFormBuilderMessage('');
                              }}
                            >
                              Remove field
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                ))}
              </div>

              <div className="admin-form-builder-footer">
                <button
                  type="button"
                  className="btn ghost"
                  onClick={() => {
                    setEditableForm((current) => ({
                      ...current,
                      sections: [
                        ...(current.sections || []),
                        buildBlankSection(current.sections.length),
                      ],
                    }));
                    setFormBuilderError('');
                    setFormBuilderMessage('');
                  }}
                >
                  Add section
                </button>
                <p className="muted">
                  Changes remain draft-safe until you save them into the governed form definition.
                </p>
              </div>

              {formBuilderError ? <p className="muted">{formBuilderError}</p> : null}
              {stateChangeMutation.error && !formBuilderError ? (
                <p className="muted">{stateChangeMutation.error.message}</p>
              ) : null}
              {formBuilderMessage ? <p className="muted">{formBuilderMessage}</p> : null}
            </div>
          )}
        </div>

        <div className="span-6 card">
          <div className="eyebrow">Rubric builder</div>
          <h2 className="card-title">Scoring rubrics</h2>
          {rubrics.length === 0 ? (
            <EmptyState title="No configured rubrics" />
          ) : (
            <ul className="admin-config-list">
              {rubrics.map((rubric) => {
                const criteria = Array.isArray(rubric.criteria) ? rubric.criteria : [];
                const totalWeight = criteria.reduce(
                  (sum, criterion) => sum + Number(criterion.weight || 0),
                  0,
                );
                return (
                  <li key={rubric.id}>
                    <div>
                      <strong>{rubric.name}</strong>
                      <p>
                        {criteria.length} criteria · v{rubric.version || 1}
                      </p>
                      {criteria.length > 0 ? (
                        <div className="admin-rubric-weight-strip" aria-label="Criterion weights">
                          {criteria.map((criterion, criterionIndex) => {
                            const weight = Number(criterion.weight || 0);
                            return (
                              <div
                                className="admin-rubric-weight-row"
                                key={`${rubric.id}-${criterion.id || criterionIndex}`}
                              >
                                <span className="admin-rubric-weight-label">
                                  {criterion.label || `Criterion ${criterionIndex + 1}`}
                                </span>
                                <InlineScoreCell
                                  value={weight}
                                  max={Math.max(totalWeight, weight, 1)}
                                  tone={bandTone(weight, { good: 25, warn: 10 })}
                                  ariaLabel={`${criterion.label || 'Criterion'} weight ${weight}`}
                                />
                              </div>
                            );
                          })}
                          <div className="admin-rubric-weight-total">
                            <span className="muted small">Total weight</span>
                            <strong>{totalWeight}</strong>
                          </div>
                        </div>
                      ) : null}
                    </div>
                    <div className="admin-config-actions">
                      <StatusChip status={rubric.status || 'draft'} />
                      <button
                        type="button"
                        className="btn ghost sm"
                        onClick={() => {
                          setSelectedRubricId(rubric.id);
                          setEditableRubric(normalizeEditableRubric(rubric));
                          setRubricBuilderError('');
                          setRubricBuilderMessage('');
                        }}
                      >
                        Edit rubric
                      </button>
                      {rubric.status !== 'active' ? (
                        <button
                          type="button"
                          className="btn ghost sm"
                          disabled={stateChangeMutation.running}
                          onClick={() => activateRubric(rubric.id)}
                        >
                          Activate rubric
                        </button>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="span-6 card">
          <div className="eyebrow">Weighted review model</div>
          <h2 className="card-title">Configure criteria and weights</h2>
          {!editableRubric ? (
            <EmptyState
              title="Select a rubric to configure"
              description="Choose a governed OWHA scoring rubric to update weighted criteria and activate the reviewed version."
            />
          ) : (
            <div className="admin-rubric-builder">
              <div className="admin-rubric-builder-toolbar">
                <FormField label="Rubric definition" htmlFor="admin-rubric-selector">
                  <Select
                    id="admin-rubric-selector"
                    value={selectedRubricId}
                    onChange={(value) => {
                      const nextRubric = rubrics.find((rubric) => rubric.id === value);
                      setSelectedRubricId(value);
                      setEditableRubric(normalizeEditableRubric(nextRubric));
                      setRubricBuilderError('');
                      setRubricBuilderMessage('');
                    }}
                    options={rubrics.map((rubric) => ({
                      value: rubric.id,
                      label: `${rubric.name} (v${rubric.version || 1})`,
                    }))}
                  />
                </FormField>
                <div className="admin-rubric-builder-toolbar-actions">
                  <button
                    type="button"
                    className="btn ghost"
                    onClick={() => {
                      setEditableRubric(
                        normalizeEditableRubric(
                          rubrics.find((rubric) => rubric.id === selectedRubricId),
                        ),
                      );
                      setRubricBuilderError('');
                      setRubricBuilderMessage('');
                    }}
                  >
                    Reset edits
                  </button>
                  <button
                    type="button"
                    className="btn primary"
                    disabled={stateChangeMutation.running}
                    onClick={async () => {
                      const issues = validateEditableRubric(editableRubric);
                      if (issues.length > 0) {
                        setRubricBuilderError(
                          `Complete the rubric before saving: ${issues.join(', ')}.`,
                        );
                        setRubricBuilderMessage('');
                        return;
                      }

                      const response = await stateChangeMutation.run(() =>
                        api.patch(`/admin/rubrics/${editableRubric.id}`, {
                          name: editableRubric.name,
                          criteria: (editableRubric.criteria || []).map(
                            (criterion, criterionIndex) => ({
                              id:
                                criterion.id ||
                                buildRubricCriterionId(criterion.label, criterionIndex),
                              label: criterion.label,
                              weight: Number(criterion.weight),
                              description: criterion.description || '',
                            }),
                          ),
                        }),
                      );
                      const savedRubric = normalizeEditableRubric(response?.data || response);
                      setEditableRubric(savedRubric);
                      setRubricBuilderError('');
                      setRubricBuilderMessage(
                        `Saved rubric definition version ${savedRubric?.version || editableRubric.version || 1}.`,
                      );
                      rubricsRes.setData((current) => {
                        if (!current?.data) return current;
                        return {
                          ...current,
                          data: current.data.map((rubric) =>
                            rubric.id === savedRubric.id ? savedRubric : rubric,
                          ),
                        };
                      });
                    }}
                  >
                    Save rubric changes
                  </button>
                  <button
                    type="button"
                    className="btn ghost"
                    disabled={stateChangeMutation.running || editableRubric.status === 'active'}
                    onClick={async () => {
                      const response = await stateChangeMutation.run(() =>
                        api.post(`/admin/rubrics/${editableRubric.id}/activate`, {}),
                      );
                      const activatedRubric = normalizeEditableRubric(response?.data || response);
                      setEditableRubric(activatedRubric);
                      setRubricBuilderError('');
                      setRubricBuilderMessage(
                        `Activated rubric version ${activatedRubric?.version || editableRubric.version || 1}.`,
                      );
                      rubricsRes.setData((current) => {
                        if (!current?.data) return current;
                        return {
                          ...current,
                          data: current.data.map((rubric) =>
                            rubric.id === activatedRubric.id ? activatedRubric : rubric,
                          ),
                        };
                      });
                    }}
                  >
                    Activate selected version
                  </button>
                </div>
              </div>

              <FormField label="Rubric name" htmlFor="admin-rubric-name">
                <TextInput
                  id="admin-rubric-name"
                  value={editableRubric.name || ''}
                  onChange={(value) => {
                    setEditableRubric((current) => ({
                      ...current,
                      name: value,
                    }));
                    setRubricBuilderError('');
                    setRubricBuilderMessage('');
                  }}
                />
              </FormField>

              <div className="admin-config-inline-meta">
                <span className="chip outline sm">{editableRubric.rubricType || 'intake'}</span>
                <span className="chip outline sm">v{editableRubric.version || 1}</span>
                <StatusChip status={editableRubric.status || 'draft'} />
              </div>

              <div className="admin-rubric-criteria-list">
                {(editableRubric.criteria || []).map((criterion, criterionIndex) => (
                  <section
                    key={`${criterion.id || 'new'}-${criterionIndex}`}
                    className="admin-rubric-criterion-card"
                    data-testid={`admin-rubric-criterion-${criterionIndex}`}
                  >
                    <FormField
                      label="Criterion label"
                      htmlFor={`admin-rubric-criterion-label-${criterionIndex}`}
                    >
                      <TextInput
                        id={`admin-rubric-criterion-label-${criterionIndex}`}
                        value={criterion.label}
                        onChange={(value) => {
                          setEditableRubric((current) => ({
                            ...current,
                            criteria: current.criteria.map((item, itemIndex) =>
                              itemIndex === criterionIndex ? { ...item, label: value } : item,
                            ),
                          }));
                          setRubricBuilderError('');
                          setRubricBuilderMessage('');
                        }}
                      />
                    </FormField>
                    <FormField
                      label="Weight"
                      htmlFor={`admin-rubric-criterion-weight-${criterionIndex}`}
                    >
                      <TextInput
                        id={`admin-rubric-criterion-weight-${criterionIndex}`}
                        value={String(criterion.weight ?? '')}
                        onChange={(value) => {
                          setEditableRubric((current) => ({
                            ...current,
                            criteria: current.criteria.map((item, itemIndex) =>
                              itemIndex === criterionIndex
                                ? {
                                    ...item,
                                    weight: Number(value || 0),
                                  }
                                : item,
                            ),
                          }));
                          setRubricBuilderError('');
                          setRubricBuilderMessage('');
                        }}
                      />
                    </FormField>
                    <div className="admin-rubric-criterion-actions">
                      <button
                        type="button"
                        className="btn ghost sm"
                        disabled={(editableRubric.criteria || []).length === 1}
                        onClick={() => {
                          setEditableRubric((current) => ({
                            ...current,
                            criteria: current.criteria.filter(
                              (_item, itemIndex) => itemIndex !== criterionIndex,
                            ),
                          }));
                          setRubricBuilderError('');
                          setRubricBuilderMessage('');
                        }}
                      >
                        Remove criterion
                      </button>
                    </div>
                  </section>
                ))}
              </div>

              <div className="admin-rubric-builder-footer">
                <button
                  type="button"
                  className="btn ghost"
                  onClick={() => {
                    setEditableRubric((current) => ({
                      ...current,
                      criteria: [...(current.criteria || []), buildBlankRubricCriterion()],
                    }));
                    setRubricBuilderError('');
                    setRubricBuilderMessage('');
                  }}
                >
                  Add criterion
                </button>
                <p className="muted">
                  Weighted rubric edits stay governed until a reviewer activates the selected
                  version.
                </p>
              </div>

              {rubricBuilderError ? <p className="muted">{rubricBuilderError}</p> : null}
              {stateChangeMutation.error && !rubricBuilderError ? (
                <p className="muted">{stateChangeMutation.error.message}</p>
              ) : null}
              {rubricBuilderMessage ? <p className="muted">{rubricBuilderMessage}</p> : null}
            </div>
          )}
        </div>

        <div className="span-12 card">
          <div className="eyebrow">Director LOE metadata</div>
          <h2 className="card-title">Configured lines of effort</h2>
          {loes.length === 0 ? (
            <EmptyState title="No LOE metadata loaded" />
          ) : (
            <ul className="admin-config-loe-list">
              {loes.map((loe) => (
                <li key={loe.id}>
                  <div>
                    <strong>{loe.label || loe.id}</strong>
                    <p>
                      {loe.description ||
                        'Configured for intake, dashboards, and briefing filters.'}
                    </p>
                    <div className="admin-config-inline-meta">
                      <span className="chip outline sm">{loe.dashboardColorToken || 'navy'}</span>
                      {loe.visibleInIntake ? <span className="chip outline sm">Intake</span> : null}
                      {loe.visibleInDashboards ? (
                        <span className="chip outline sm">Dashboards</span>
                      ) : null}
                      {loe.visibleInReports ? (
                        <span className="chip outline sm">Reports</span>
                      ) : null}
                    </div>
                  </div>
                  <StatusChip status={loe.status || 'active'} />
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="span-7 card">
          <div className="eyebrow">Template configuration</div>
          <h2 className="card-title">Edit governed report metadata</h2>
          {!editableReportTemplate ? (
            <EmptyState
              title="No report template selected"
              description="Load a governed report template to tune export posture, ownership, and AI usage controls."
            />
          ) : (
            <div className="admin-report-template-builder">
              <div className="admin-report-template-toolbar">
                <FormField
                  label="Report template"
                  htmlFor="report-template-selector"
                  hint="Select the governed template definition you want to update."
                >
                  <Select
                    id="report-template-selector"
                    value={selectedReportTemplateId}
                    onChange={(value) => {
                      setSelectedReportTemplateId(value);
                      setEditableReportTemplate(
                        normalizeEditableReportTemplate(
                          reportTemplateRegistry.find((template) => template.id === value),
                        ),
                      );
                      setReportTemplateBuilderError('');
                      setReportTemplateBuilderMessage('');
                    }}
                    options={reportTemplateRegistry.map((template) => ({
                      value: template.id,
                      label: `${template.name} (${template.id})`,
                    }))}
                  />
                </FormField>

                <div className="admin-report-template-actions">
                  <button
                    type="button"
                    className="btn primary"
                    disabled={stateChangeMutation.running}
                    onClick={async () => {
                      const issues = validateEditableReportTemplate(editableReportTemplate);
                      if (issues.length > 0) {
                        setReportTemplateBuilderError(
                          `Complete the following before saving: ${issues.join(', ')}.`,
                        );
                        setReportTemplateBuilderMessage('');
                        return;
                      }

                      try {
                        const response = await stateChangeMutation.run(() =>
                          api.patch(
                            `/admin/report-templates/${editableReportTemplate.id}`,
                            editableReportTemplate,
                          ),
                        );
                        const savedTemplate = response?.data || editableReportTemplate;
                        reportTemplatesRes.setData((current) => ({
                          ...(current || {}),
                          data: upsertCollectionItem(current?.data, savedTemplate),
                          page: current?.page
                            ? {
                                ...current.page,
                                total: Math.max(
                                  Number(current.page.total || 0),
                                  reportTemplates.length,
                                ),
                              }
                            : { limit: 100, offset: 0, total: reportTemplates.length },
                        }));
                        setReportTemplateRegistry((current) =>
                          upsertCollectionItem(current, savedTemplate),
                        );
                        await reportTemplatesRes.refresh();
                        setEditableReportTemplate(normalizeEditableReportTemplate(savedTemplate));
                        setSelectedReportTemplateId(savedTemplate.id);
                        setReportTemplateBuilderError('');
                        setReportTemplateBuilderMessage(
                          `Saved report template version ${savedTemplate.version || 1}.`,
                        );
                      } catch (error) {
                        setReportTemplateBuilderError(
                          error?.message || 'Unable to save report template changes.',
                        );
                        setReportTemplateBuilderMessage('');
                      }
                    }}
                  >
                    Save report template
                  </button>
                  {editableReportTemplate.status !== 'active' ? (
                    <button
                      type="button"
                      className="btn ghost"
                      disabled={stateChangeMutation.running}
                      onClick={() => activateReportTemplate(editableReportTemplate.id)}
                    >
                      Activate selected template
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="admin-config-create-grid">
                <FormField
                  label="Template name"
                  htmlFor="report-template-name"
                  required
                  hint="Governed display name used across briefing and export workflows."
                >
                  <TextInput
                    id="report-template-name"
                    value={editableReportTemplate.name || ''}
                    onChange={(value) => {
                      setEditableReportTemplate((current) => ({ ...current, name: value }));
                      setReportTemplateBuilderError('');
                      setReportTemplateBuilderMessage('');
                    }}
                  />
                </FormField>

                <FormField
                  label="Owner user ID"
                  htmlFor="report-template-owner"
                  hint="Synthetic accountable owner for approvals and stewardship."
                >
                  <TextInput
                    id="report-template-owner"
                    value={editableReportTemplate.ownerUserId || ''}
                    onChange={(value) => {
                      setEditableReportTemplate((current) => ({ ...current, ownerUserId: value }));
                      setReportTemplateBuilderError('');
                      setReportTemplateBuilderMessage('');
                    }}
                  />
                </FormField>
              </div>

              <FormField
                label="Template description"
                htmlFor="report-template-description"
                required
                hint="Describe when leadership should use this template and what decision context it supports."
              >
                <Textarea
                  id="report-template-description"
                  rows={3}
                  value={editableReportTemplate.description || ''}
                  onChange={(value) => {
                    setEditableReportTemplate((current) => ({ ...current, description: value }));
                    setReportTemplateBuilderError('');
                    setReportTemplateBuilderMessage('');
                  }}
                />
              </FormField>

              <div className="admin-config-create-grid">
                <FormField
                  label="Sensitivity"
                  htmlFor="report-template-sensitivity"
                  hint="Display masking and export handling should follow this posture."
                >
                  <Select
                    id="report-template-sensitivity"
                    value={editableReportTemplate.sensitivity || 'CUI'}
                    onChange={(value) => {
                      setEditableReportTemplate((current) => ({ ...current, sensitivity: value }));
                      setReportTemplateBuilderError('');
                      setReportTemplateBuilderMessage('');
                    }}
                    options={REPORT_TEMPLATE_SENSITIVITY_OPTIONS}
                  />
                </FormField>

                <FormField
                  label="Product type"
                  htmlFor="report-template-product-type"
                  hint="Product-family identifier used by the governed product factory."
                >
                  <TextInput
                    id="report-template-product-type"
                    value={editableReportTemplate.productType || ''}
                    onChange={(value) => {
                      setEditableReportTemplate((current) => ({ ...current, productType: value }));
                      setReportTemplateBuilderError('');
                      setReportTemplateBuilderMessage('');
                    }}
                  />
                </FormField>
              </div>

              <div className="admin-config-checkbox-stack">
                <label className="admin-config-checkbox" htmlFor="report-template-human-review">
                  <input
                    id="report-template-human-review"
                    type="checkbox"
                    checked={editableReportTemplate.humanReviewRequired !== false}
                    onChange={(event) => {
                      setEditableReportTemplate((current) => ({
                        ...current,
                        humanReviewRequired: event.target.checked,
                      }));
                      setReportTemplateBuilderError('');
                      setReportTemplateBuilderMessage('');
                    }}
                  />
                  <span>Require human review before official use</span>
                </label>
                <label className="admin-config-checkbox" htmlFor="report-template-ai-allowed">
                  <input
                    id="report-template-ai-allowed"
                    type="checkbox"
                    checked={editableReportTemplate.aiAllowed !== false}
                    onChange={(event) => {
                      setEditableReportTemplate((current) => ({
                        ...current,
                        aiAllowed: event.target.checked,
                      }));
                      setReportTemplateBuilderError('');
                      setReportTemplateBuilderMessage('');
                    }}
                  />
                  <span>Allow AI-assisted drafting</span>
                </label>
              </div>

              <div className="admin-report-template-format-group">
                <span className="form-label">Approved output formats</span>
                <div className="admin-report-template-format-grid" role="group">
                  {REPORT_TEMPLATE_FORMAT_OPTIONS.map((formatOption) => {
                    const checked = (editableReportTemplate.outputFormats || []).includes(
                      formatOption.value,
                    );
                    return (
                      <label
                        key={formatOption.value}
                        className="admin-config-checkbox"
                        htmlFor={`report-template-format-${formatOption.value}`}
                      >
                        <input
                          id={`report-template-format-${formatOption.value}`}
                          type="checkbox"
                          checked={checked}
                          onChange={(event) => {
                            setEditableReportTemplate((current) => ({
                              ...current,
                              outputFormats: event.target.checked
                                ? Array.from(
                                    new Set([
                                      ...(current?.outputFormats || []),
                                      formatOption.value,
                                    ]),
                                  )
                                : (current?.outputFormats || []).filter(
                                    (format) => format !== formatOption.value,
                                  ),
                            }));
                            setReportTemplateBuilderError('');
                            setReportTemplateBuilderMessage('');
                          }}
                        />
                        <span>{`${formatOption.label} output`}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="admin-report-template-context">
                <div>
                  <span className="form-label">Allowed scopes</span>
                  <ChipMultiSelect
                    options={(editableReportTemplate.allowedScopes || []).map((scope) => ({
                      value: scope,
                      label: startCase(scope),
                    }))}
                    selected={editableReportTemplate.allowedScopes || []}
                    disabled
                  />
                </div>
                <div>
                  <span className="form-label">Required sections</span>
                  <ChipMultiSelect
                    options={(editableReportTemplate.requiredSections || []).map((section) => ({
                      value: section,
                      label: startCase(section),
                    }))}
                    selected={editableReportTemplate.requiredSections || []}
                    disabled
                  />
                </div>
              </div>

              <div className="admin-config-inline-meta">
                <span className="chip outline sm">{editableReportTemplate.productType}</span>
                <span className="chip outline sm">v{editableReportTemplate.version || 1}</span>
                <StatusChip status={editableReportTemplate.status || 'draft'} />
              </div>

              <p className="muted">
                Template edits stay versioned and audited. Human review gates still apply before any
                official export can be used.
              </p>
              {reportTemplateBuilderError ? (
                <p className="muted">{reportTemplateBuilderError}</p>
              ) : null}
              {stateChangeMutation.error && !reportTemplateBuilderError ? (
                <p className="muted">{stateChangeMutation.error.message}</p>
              ) : null}
              {reportTemplateBuilderMessage ? (
                <p className="muted">{reportTemplateBuilderMessage}</p>
              ) : null}
            </div>
          )}
        </div>

        <div className="span-5 card">
          <div className="eyebrow">Report and export registry</div>
          <h2 className="card-title">Governed report templates</h2>
          {reportTemplateRegistry.length === 0 ? (
            <BrandedEmptyState
              icon="🗂️"
              eyebrow="Report registry"
              title="No report templates loaded"
              description="Load or author report metadata to govern decks, papers, reports, and closeout products."
            />
          ) : (
            <ul className="admin-config-loe-list">
              {reportTemplateRegistry.map((template) => (
                <li key={template.id}>
                  <div>
                    <strong>{template.name}</strong>
                    <p>{template.description || 'Configured export template metadata.'}</p>
                    <div className="admin-config-inline-meta">
                      <span className="chip outline sm">{template.productType || 'report'}</span>
                      <span className="chip outline sm">v{template.version || 1}</span>
                      {(template.outputFormats || []).map((format) => (
                        <span key={`${template.id}-${format}`} className="chip outline sm">
                          {String(format).toUpperCase()}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="admin-config-actions">
                    <StatusChip status={template.status || 'draft'} />
                    {template.status !== 'active' ? (
                      <button
                        type="button"
                        className="btn ghost sm"
                        disabled={stateChangeMutation.running}
                        onClick={() => activateReportTemplate(template.id)}
                      >
                        Activate report template
                      </button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </>
  );
}
